import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'
import uiConstants from 'config/constants/ui'
import ActivityView from 'src/apps/Messages/apps/ActivityView'
import ThreadChatView from 'src/apps/Messages/apps/ThreadChatView'
import { InitiativeHierarchyContext, withInitiativeHierarchyProvider }
   from 'src/libraries/contexts/InitiativeHierarchy'
import NotebookContext from 'src/libraries/contexts/Notebook'
import { useNoteList } from 'src/graphql/hooks'
import NoteCreate from '~/apps/NoteCreate'
import NoteEdit from '~/apps/NoteEdit'
import NoteContainer from '~/components/NoteContainer'
import { calcRenderedNotes, determineNoteIndex, nextIndex, prevIndex } from '~/components/utils'

const Notebook = ({ notebookId }) => {
   const history = useHistory()
   const { notebookSlug, noteId } = useParams()
   const { useFacingPages, setVisibleNoteIds } = useContext(NotebookContext)

   // Number of notes to be rendered on the DOM
   const numNotesRendered = useFacingPages ? 8 : 5

   // TODO: Add on-demand loading
   // See: #11
   const { noteList, refetch: refetchNoteList } = useNoteList({
      initiativeId: notebookId,
      limit: 100
   }, { skip: !notebookId })

   const notes = noteList.items
   const noteIds = useRef(notes.map(n => n.id))

   // If using facing pages, currentIndex should always be the left note
   // (which is an even index)
   const [currentIndex, setCurrentIndex] = useState(null)

   useEffect(() => {
      noteIds.current = noteList.items.map(n => n.id)
      setCurrentIndex(noteId
         ? determineNoteIndex(noteIds.current, noteId, useFacingPages)
         : 0)
   }, [noteList])

   // This state prop stores which notes are rendered to the DOM
   // (based on the current index)
   const [notesRendered, setNotesRendered] = useState([])

   // If the note id param changes in the url, then figure out the appropriate
   // note index.
   useEffect(() => {
      const newCurrentIndex = noteId
         ? determineNoteIndex(noteIds.current, noteId, useFacingPages)
         : 0

      setCurrentIndex(newCurrentIndex)
      setNotesRendered(calcRenderedNotes(newCurrentIndex, notes, numNotesRendered))
   }, [notes, noteId])

   // Figure out which note(s) are currently being displayed and update
   // the global context.
   useEffect(() => {
      const newVisibleNoteIds = [noteList.items[currentIndex]?.id]
      if (useFacingPages) {
         const adjacentNoteId = currentIndex % 2 === 0
            ? noteList.items[currentIndex + 1]?.id
            : noteList.items[currentIndex - 1]?.id

         if (adjacentNoteId) {
            newVisibleNoteIds.push(adjacentNoteId)
         }
      }

      setVisibleNoteIds(newVisibleNoteIds)
   }, [currentIndex, noteList.items, useFacingPages])

   // Keyboard Events
   const handleKeydown = useCallback((e) => {
      const tag = e.target.tagName.toLowerCase()
      const isContentEditable = e.target.contentEditable === 'true'
      if (isContentEditable || tag === 'input' || tag === 'textarea') {
         return
      }

      if (e.keyCode === 37) {
         // Left
         e.stopPropagation()
         const newIndex = prevIndex(currentIndex, useFacingPages)
         const newNoteId = noteIds.current[newIndex]
         setCurrentIndex(newIndex)
         history.replace(`/notebook/${notebookSlug}/${newNoteId}`)
      } else if (e.keyCode === 39) {
         // Right
         e.stopPropagation()
         const newIndex = nextIndex(currentIndex, notes.length, useFacingPages)
         const newNoteId = noteIds.current[newIndex]
         setCurrentIndex(newIndex)
         history.replace(`/notebook/${notebookSlug}/${newNoteId}`)
      }
   }, [notes, currentIndex, useFacingPages])

   useEffect(() => {
      window.addEventListener('keydown', handleKeydown)

      // Remove event listeners on component unmount
      return () => {
         window.removeEventListener('keydown', handleKeydown)
      }
   }, [handleKeydown])

   return (
      <>
         {noteId === 'create' && (
            <NoteContainer
               zIndex={uiConstants.zIndexes.noteStack}
               facingLeft={false}
               facingRight={false}
               offscreen={false}>
               <NoteCreate
                  onCreate={async (response) => {
                     await refetchNoteList()
                     // eslint-disable-next-line react/prop-types
                     history.push(`/notebook/${notebookSlug}/${response.data.experimentalCreateNote.id}`)
                  }}
               />
            </NoteContainer>
         )}
         {notesRendered.map((note, index) => {
            const noteIndex = noteIds.current.indexOf(note.id)
            return (
               <NoteContainer
                  key={note.id}
                  zIndex={uiConstants.zIndexes.noteStack - index - 1}
                  facingLeft={useFacingPages && noteIndex % 2 === 0}
                  facingRight={useFacingPages && noteIndex % 2 === 1}
                  offscreen={noteIndex < currentIndex}>
                  <NoteEdit
                     id={note.id}
                     onRemove={() => history.push('/')}
                  />
               </NoteContainer>
            )
         })}
      </>
   )
}

// =======================================================
// Export
// (with a tiny wrapper that maps notebookSlug to notebookId)
// =======================================================

export default withInitiativeHierarchyProvider({ slugParam: 'notebookSlug' })(() => {
   const { setCurrentNotebook } = useContext(NotebookContext)
   const { initiative, initiativeLoading } = useContext(InitiativeHierarchyContext)

   if (initiativeLoading) {
      return null
   }

   useEffect(() => {
      setCurrentNotebook(initiative)
   }, [initiative.slug])

   return (
      <Switch>
         <Route path="/notebook/:notebookSlug/activity" component={ActivityView} />
         <Route path="/notebook/:notebookSlug/messages/:threadId" component={ThreadChatView} />
         <Route path="/notebook/:notebookSlug/:noteId">
            <Notebook notebookId={initiative.id} />
         </Route>
      </Switch>
   )
})
